import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import type { ReactNode } from "react";

import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// ===========================================================================
// La cabecera de trabajo de la propuesta (`.cp` en `fusion.css`).
//
// Tres piezas en dos filas:
//
//   1. Migas de pan — de Odoo — con el título de la página al final, y a la
//      derecha el selector de periodo con sus flechas.
//   2. Las acciones de la página y, ocupando el resto, el buscador.
//   3. Dentro del buscador, las FACETAS: lo que ya está filtrado se ve como
//      una etiqueta con su clave y su valor, y se quita con la ✕.
//
// La faceta es lo que hace que un filtro activo no sea invisible. Una lista
// filtrada sin decir por qué es la forma más fácil de que alguien crea que
// faltan datos.
//
// `menus` viene VACÍO por defecto, y es a propósito. Traía
// `["Filtros", "Agrupar por", "Favoritos"]` copiados de la lámina, dibujados
// como `<span>` con su flecha: no eran botones, no abrían nada y salían en las
// nueve pantallas que usan esta cabecera. Contabilidad los contó entre «los
// botones que no funcionan». Un menú que no se puede abrir cuesta más que la
// función que promete, así que hasta que haya uno de verdad no se pinta
// ninguno. El parámetro se queda para cuando lo haya.
// ===========================================================================

export type Faceta = {
  clave: string;
  valor: string;
  /** Sin esto la faceta no se puede quitar: es un filtro fijo. */
  alQuitar?: () => void;
  tono?: "acento" | "gris";
};

export function Cabecera({
  migas,
  titulo,
  periodo,
  alCambiarPeriodo,
  acciones,
  facetas = [],
  buscar,
  alBuscar,
  marcador,
  menus = [],
}: {
  migas?: string[];
  titulo: string;
  periodo?: string;
  alCambiarPeriodo?: (direccion: -1 | 1) => void;
  acciones?: ReactNode;
  facetas?: Faceta[];
  buscar?: string;
  alBuscar?: (v: string) => void;
  marcador?: string;
  menus?: string[];
}) {
  return (
    <div className="space-y-3">
      {/* Fila 1 · migas y periodo */}
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 items-center gap-2 text-[12.5px] text-muted-foreground">
          {migas?.map((m) => (
            <span key={m} className="flex items-center gap-2">
              {m}
              <span className="text-border">/</span>
            </span>
          ))}
          <b className="truncate font-semibold text-[19px] text-foreground tracking-tight">
            {titulo}
          </b>
        </div>

        {periodo ? (
          <div className="ml-auto flex shrink-0 items-center gap-1.5 text-[12px] text-muted-foreground">
            Periodo <b className="text-foreground">{periodo}</b>
            <button
              type="button"
              onClick={() => alCambiarPeriodo?.(-1)}
              disabled={!alCambiarPeriodo}
              aria-label="Periodo anterior"
              className="grid size-[22px] place-items-center rounded-[5px] border disabled:opacity-40"
            >
              <ChevronLeft className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => alCambiarPeriodo?.(1)}
              disabled={!alCambiarPeriodo}
              aria-label="Periodo siguiente"
              className="grid size-[22px] place-items-center rounded-[5px] border disabled:opacity-40"
            >
              <ChevronRight className="size-3" />
            </button>
          </div>
        ) : null}
      </div>

      {/* Fila 2 · acciones y buscador */}
      <div className="flex flex-wrap items-center gap-2.5">
        {acciones}

        {/* Un buscador que no busca nada NO SE DIBUJA.
            Antes bastaba con un `marcador` para que apareciera la caja, y sin
            `alBuscar` el input salía `disabled` con un texto que decía
            «Filtrar lo que se ve en esta pantalla…». Tres pantallas lo hacían
            —portada, documentos y cierre— y contabilidad las contó entre los
            filtros que no funcionan. Un campo que invita a escribir y no acepta
            teclas es peor que no tenerlo.

            La barra se dibuja cuando hay algo que enseñar —facetas— o algo que
            escribir —`alBuscar`—, y el input solo cuando de verdad busca. Las
            facetas se quedan aunque no haya buscador: son informativas, dicen
            por qué la lista está recortada. */}
        {facetas.length > 0 || alBuscar ? (
          <div className="flex h-[34px] min-w-0 flex-1 items-center gap-1.5 rounded-md border bg-background px-2">
            <Search className="size-[15px] shrink-0 text-muted-foreground" />

            {facetas.map((f) => (
              <FacetaEtiqueta key={`${f.clave}-${f.valor}`} {...f} />
            ))}

            {alBuscar ? (
              <input
                value={buscar ?? ""}
                onChange={(e) => alBuscar(e.target.value)}
                placeholder={marcador ?? "Buscar…"}
                className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
              />
            ) : (
              <span className="min-w-0 flex-1" />
            )}

            {menus.length > 0 ? (
              <div className="ml-auto hidden shrink-0 items-center gap-3 md:flex">
                {menus.map((m) => (
                  <span
                    key={m}
                    className="flex items-center gap-1 text-[12px] text-muted-foreground"
                  >
                    {m}
                    <ChevronDown className="size-[11px]" />
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <Separator />
    </div>
  );
}

/** La etiqueta de un filtro activo: clave en color, valor al lado y la ✕. */
function FacetaEtiqueta({ clave, valor, alQuitar, tono = "acento" }: Faceta) {
  const esAcento = tono === "acento";
  return (
    <span
      className={cn(
        "flex shrink-0 items-stretch overflow-hidden rounded-[5px] border text-[11.5px]",
        esAcento ? "border-cyan-500" : "border-border",
      )}
    >
      <span
        className={cn(
          "grid place-items-center px-1.5 py-0.5 font-bold text-[10px] uppercase tracking-wide",
          esAcento
            ? "bg-cyan-500 text-cyan-950"
            : "bg-muted-foreground text-background",
        )}
      >
        {clave}
      </span>
      <span
        className={cn(
          "px-2 py-0.5 font-semibold",
          esAcento
            ? "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300"
            : "bg-muted text-muted-foreground",
        )}
      >
        {valor}
      </span>
      {alQuitar ? (
        <button
          type="button"
          onClick={alQuitar}
          aria-label={`Quitar el filtro ${clave}`}
          className={cn(
            "px-1.5",
            esAcento
              ? "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300"
              : "bg-muted text-muted-foreground",
          )}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}
